// SPDX-License-Identifier: AGPL-3.0-or-later

import type {FavoriteMeme} from '@app/features/expressions/models/FavoriteMeme';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import {MemesPickerStateSchema} from '@app/vendor/schema/gen/fluxer/user/preferences/v1/pickers_pb';
import {makeAutoObservable} from 'mobx';

type MemeUsageEntry = Readonly<{
	count: number;
	lastUsed: number;
}>;
const MAX_FRECENT_MEMES = 21;
const FRECENCY_TIME_DECAY_HOURS = 24 * 7;
const logger = new Logger('MemesPicker');
class MemesPicker {
	memeUsage: Record<string, MemeUsageEntry> = {};
	favoriteMemes: Array<string> = [];
	collapsedCategories: Array<string> = [];
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
	}
	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'memesPicker',
			schema: MemesPickerStateSchema,
			persist: ['memeUsage', 'favoriteMemes', 'collapsedCategories'],
			toMessage: (s) => ({
				usage: Object.fromEntries(
					Object.entries(s.memeUsage).map(([key, entry]) => [
						key,
						{count: entry.count, lastUsedMs: BigInt(entry.lastUsed)},
					]),
				),
				favoriteMemeIds: [...s.favoriteMemes],
				collapsedCategoryIds: [...s.collapsedCategories],
			}),
			applyMessage: (s, m) => {
				const usage: Record<string, MemeUsageEntry> = {};
				for (const [key, stat] of Object.entries(m.usage)) {
					usage[key] = {count: stat.count, lastUsed: Number(stat.lastUsedMs)};
				}
				s.memeUsage = usage;
				s.favoriteMemes = [...m.favoriteMemeIds];
				s.collapsedCategories = [...m.collapsedCategoryIds];
			},
			mergeForMigration: (local, server) => {
				const usage: Record<
					string,
					{
						count: number;
						lastUsedMs: bigint;
					}
				> = {};
				for (const [key, stat] of Object.entries(local.usage)) {
					usage[key] = {count: stat.count, lastUsedMs: stat.lastUsedMs};
				}
				for (const [key, stat] of Object.entries(server.usage)) {
					const existing = usage[key];
					if (existing === undefined) {
						usage[key] = {count: stat.count, lastUsedMs: stat.lastUsedMs};
					} else {
						usage[key] = {
							count: Math.max(existing.count, stat.count),
							lastUsedMs: existing.lastUsedMs > stat.lastUsedMs ? existing.lastUsedMs : stat.lastUsedMs,
						};
					}
				}
				return {
					usage,
					favoriteMemeIds: Array.from(new Set([...local.favoriteMemeIds, ...server.favoriteMemeIds])),
					collapsedCategoryIds: Array.from(new Set([...local.collapsedCategoryIds, ...server.collapsedCategoryIds])),
				};
			},
		});
	}
	trackMemeUsage(memeKey: string): void {
		const now = Date.now();
		const currentUsage = this.memeUsage[memeKey];
		const newCount = (currentUsage?.count ?? 0) + 1;
		this.memeUsage[memeKey] = {
			count: newCount,
			lastUsed: now,
		};
	}
	toggleFavorite(memeKey: string): void {
		if (this.favoriteMemes.includes(memeKey)) {
			const index = this.favoriteMemes.indexOf(memeKey);
			if (index > -1) {
				this.favoriteMemes.splice(index, 1);
			}
		} else {
			this.favoriteMemes.push(memeKey);
		}
		ComponentDispatch.dispatch('MEMES_PICKER_RERENDER');
		logger.debug(`Toggled favorite meme: ${memeKey}`);
	}
	toggleCategory(category: string): void {
		if (this.collapsedCategories.includes(category)) {
			const index = this.collapsedCategories.indexOf(category);
			if (index > -1) {
				this.collapsedCategories.splice(index, 1);
			}
		} else {
			this.collapsedCategories.push(category);
		}
		ComponentDispatch.dispatch('MEMES_PICKER_RERENDER');
		logger.debug(`Toggled category: ${category}`);
	}
	isFavorite(meme: FavoriteMeme): boolean {
		return this.favoriteMemes.includes(this.getMemeKey(meme));
	}
	isCategoryCollapsed(categoryId: string): boolean {
		return this.collapsedCategories.includes(categoryId);
	}
	private getFrecencyScore(entry: MemeUsageEntry): number {
		const now = Date.now();
		const hoursSinceLastUse = (now - entry.lastUsed) / (1000 * 60 * 60);
		const timeDecay = Math.max(0, 1 - hoursSinceLastUse / FRECENCY_TIME_DECAY_HOURS);
		return entry.count * (1 + timeDecay);
	}
	getFrecentMemes(allMemes: ReadonlyArray<FavoriteMeme>, limit: number = MAX_FRECENT_MEMES): Array<FavoriteMeme> {
		const memeScores: Array<{
			meme: FavoriteMeme;
			score: number;
		}> = [];
		for (const meme of allMemes) {
			const memeKey = this.getMemeKey(meme);
			const usage = this.memeUsage[memeKey];
			if (usage) {
				const score = this.getFrecencyScore(usage);
				memeScores.push({meme, score});
			}
		}
		memeScores.sort((a, b) => b.score - a.score);
		return memeScores.slice(0, limit).map((item) => item.meme);
	}
	getFavoriteMemes(allMemes: ReadonlyArray<FavoriteMeme>): Array<FavoriteMeme> {
		const favorites: Array<FavoriteMeme> = [];
		for (const meme of allMemes) {
			if (this.isFavorite(meme)) {
				favorites.push(meme);
			}
		}
		return favorites;
	}
	getFrecencyScoreForMeme(meme: FavoriteMeme): number {
		const usage = this.memeUsage[this.getMemeKey(meme)];
		return usage ? this.getFrecencyScore(usage) : 0;
	}
	private getMemeKey(meme: FavoriteMeme): string {
		return meme.id;
	}
}
export default new MemesPicker();
